import {
  addDayOffset,
  DateString,
  dateStringFromDate,
  DayOfWeek,
  getDayOfWeek,
  getFirstDayOfMonth,
  getHymnOfTheMonthDate,
  getLastDayOfMonth,
  mostRecentSunday,
  thisSundayDate,
} from './date_string.ts';
import {
  HymnOfTheMonthSongList,
  IntervalType,
  Org,
  OrgType,
  RepeatKind,
  SongList,
  SongListEntry,
  SongListEntryType,
  SongListSchemaEntry,
  SongListType,
} from './model.ts';
import {
  getSundayOrdinal,
  isBlankEntry,
  isSameSong,
  isWeeklySongListForDate,
  withoutBlankEntries,
} from './model_util.ts';
import { getFillStartSunday } from './passed_sunday_clock.ts';
import { ensureUnreachable } from './util.ts';

// Church liturgy rows that repeat (issues #4170, #4171, #4191). A church row's songs live in the Sundays' weekly
// worship service lists and nowhere else; the repeat kind on the schema row (SongListSchemaEntry.repeat) is a display
// and edit rule, so switching it writes the schema only and never changes a song list.
//
// A Sunday "holds something" at a row when its weekly list has an entry there: a song, a text or a blank (blankEntry,
// issue #4190). A Sunday before the current one (thisSundayDate(today)) shows exactly what it holds. From the current
// Sunday on, a Sunday that holds nothing shows the nearest earlier Sunday that holds something within the kind's
// scope: any earlier Sunday for Same every Sunday, an earlier Sunday of the same month for Same all month, an earlier
// Sunday of the same ordinal for By Sunday of the month. A 5th Sunday with no earlier 5th Sunday holding something
// borrows what its own month's 1st Sunday shows (resolved like any cell): that Sunday's entry when it holds one, its
// fill when it is filled, and nothing when it shows nothing, as a passed 1st Sunday written down holding nothing does.
// Where the fill reaches the 1st Sunday, that is the nearest earlier 1st Sunday's entry, as #4191 specified; it differs
// only for a 1st Sunday the fill does not reach (a passed one, or one before the row's repeatFrom) that holds nothing,
// so a 1st Sunday written down holding nothing and its 5th Sunday agree (issue #4192). A blank shows nothing and stops
// the fill until the next entry. A Does-not-repeat row never fills. Schools have no repeating rows: every reader here
// returns a school's data as stored.
//
// Once a Sunday has passed, what it showed is written into its list (getPassedSundayWrites in passed_sundays.ts, issue
// #4192). Until that write has happened a passed Sunday goes on showing its fill: the fill applies from `fillStart`
// (getFillStartSunday), the Sunday after the last one the server has written the church down through, or, for a
// reader that does not know it, the Sunday before the current one until Monday noon UTC, so nothing goes blank in
// between. The edit rules (getReachEnd) keep the current Sunday: a passed Sunday still selects and edits alone. A row
// also fills only from its `repeatFrom` (getRepeatFrom), the admin's current Sunday when its kind was chosen, so a kind
// chosen after a Sunday passed never fills that Sunday, whether on screen in the window or in the write, and a Sunday
// before it edits alone too (getReachEnd).

const repeatKinds: readonly string[] = Object.values(RepeatKind);

export function getRepeatKind(row: SongListSchemaEntry | undefined): RepeatKind {
  // a kind this bundle does not know (a newer one, saved by a newer bundle) reads as a plain row rather than throwing
  return row?.repeat !== undefined && repeatKinds.includes(row.repeat) ? row.repeat : RepeatKind.DoesNotRepeat;
}

// whether the row can be shown as a Hymn of the Month: a Song-type Same-all-month row, the only row the planner offers
// Show as Hymn of the Month on (issue #4210)
export function canShowAsHymnOfTheMonth(row: SongListSchemaEntry | undefined): boolean {
  return (
    getRepeatKind(row) === RepeatKind.SameAllMonth && (row?.type ?? SongListEntryType.Song) === SongListEntryType.Song
  );
}

// whether the row is a Hymn of the Month row: one that can be shown as a Hymn of the Month (canShowAsHymnOfTheMonth)
// and has Show as Hymn of the Month set. The flag is remembered on the row whatever its kind and type, so a starred row
// switched to another kind or type and back is a Hymn of the Month row again (issue #4210)
export function isHymnOfTheMonthRow(row: SongListSchemaEntry | undefined): boolean {
  return canShowAsHymnOfTheMonth(row) && !!row?.showAsHymnOfTheMonth;
}

// the first Sunday the row's repeat applies to (SongListSchemaEntry.repeatFrom) when its kind repeats, else undefined:
// a row whose kind was chosen before issue #4192 has none and fills from any Sunday
export function getRepeatFrom(row: SongListSchemaEntry | undefined): DateString | undefined {
  return getRepeatKind(row) === RepeatKind.DoesNotRepeat ? undefined : row?.repeatFrom;
}

// whether the kind draws runs, whose edits show through a reach of Sundays (Same every Sunday, Same all month), rather
// than one cell per Sunday (Does not repeat, By Sunday of the month)
export function isRunKind(kind: RepeatKind): boolean {
  return kind === RepeatKind.SameEverySunday || kind === RepeatKind.SameAllMonth;
}

// true for a church whose liturgy has a row that repeats, so its readers need the earlier Sundays the fill reads from
export function hasRepeatingRow(org: Pick<Org, 'type' | 'churchLiturgySchema'> | undefined): boolean {
  return (
    org?.type === OrgType.Church &&
    (org.churchLiturgySchema?.rows ?? []).some((row) => getRepeatKind(row) !== RepeatKind.DoesNotRepeat)
  );
}

// one row's entries in a church's weekly worship service lists, keyed by the list's date, blanks included: built once
// per row so resolving many cells does not rescan every list. Only Sunday services count: an old weekday service
// (worship services are Sundays only since 2025-03-01, see validateSongList on the server) neither fills nor ends a
// reach.
export class RowEntries {
  private readonly entries = new Map<DateString, SongListEntry>();
  private readonly dates: DateString[]; // ascending

  constructor(rowIndex: number, songLists: SongList[]) {
    for (const songList of songLists) {
      if (
        songList.type !== SongListType.WorshipService ||
        getDayOfWeek(songList.date) !== DayOfWeek.Sunday ||
        this.entries.has(songList.date)
      ) {
        continue;
      }
      const entry = songList.songs.find(({ row }) => row === rowIndex);
      if (entry) {
        this.entries.set(songList.date, entry);
      }
    }
    this.dates = [...this.entries.keys()].sort();
  }

  // what `date` holds at the row: a song, a text, a blank, or undefined when it holds nothing
  get(date: DateString): SongListEntry | undefined {
    return this.entries.get(date);
  }

  // the nearest Sunday before `date` that holds something, among those `inScope` accepts; the search stops at the
  // first Sunday `isBeyond` rejects (a scope bounded by the month need not walk further back)
  findEarlier(
    date: DateString,
    inScope: (date: DateString) => boolean,
    isBeyond: (date: DateString) => boolean = () => false,
  ): DateString | undefined {
    for (let index = this.indexAfter(date) - 1; index >= 0; index--) {
      const candidate = this.dates[index];
      if (candidate >= date) {
        continue;
      }
      if (isBeyond(candidate)) {
        return undefined;
      }
      if (inScope(candidate)) {
        return candidate;
      }
    }
    return undefined;
  }

  // the nearest Sunday after `date` that holds something, or undefined
  findLater(date: DateString): DateString | undefined {
    return this.dates[this.indexAfter(date)];
  }

  // the index of the first date after `date`
  private indexAfter(date: DateString): number {
    let low = 0;
    let high = this.dates.length;
    while (low < high) {
      const middle = (low + high) >> 1;
      if (this.dates[middle] <= date) {
        low = middle + 1;
      } else {
        high = middle;
      }
    }
    return low;
  }
}

export function getRowEntries(rowIndex: number, songLists: SongList[]): RowEntries {
  return new RowEntries(rowIndex, songLists);
}

export interface RepeatingCell {
  entry?: SongListEntry; // what the cell shows: never a blank, undefined when nothing shows
  isSet: boolean; // the Sunday itself holds something (a song, a text or a blank)
  sourceDate?: DateString; // the earlier Sunday a cell that holds nothing is filled from
  borrowsFirstSunday: boolean; // a By-Sunday-of-the-month 5th Sunday showing what its month's 1st Sunday shows
}

export interface RepeatingCellQuery {
  kind: RepeatKind;
  date: DateString; // a Sunday
  currentSunday: DateString;
  // the first Sunday the fill applies to: `currentSunday`, or the Sunday before it while that Sunday is not yet
  // written down (getFillStartSunday, issue #4192); `currentSunday` when absent
  fillStart?: DateString;
  // the first Sunday the row's kind applies to (getRepeatFrom, issue #4192): a Sunday before it is not filled; any
  // Sunday when absent
  repeatFrom?: DateString;
  entries: RowEntries;
}

function shownEntry(entry: SongListEntry | undefined): SongListEntry | undefined {
  return isBlankEntry(entry) ? undefined : entry;
}

// what a repeating row's cell on `date` shows (see the module comment)
export function resolveRepeatingCell(query: RepeatingCellQuery): RepeatingCell {
  const { kind, date, currentSunday, fillStart = currentSunday, repeatFrom, entries } = query;
  const own = entries.get(date);
  const isSet = own !== undefined;
  if (
    isSet ||
    date < fillStart ||
    (repeatFrom !== undefined && date < repeatFrom) ||
    kind === RepeatKind.DoesNotRepeat
  ) {
    return { entry: shownEntry(own), isSet, borrowsFirstSunday: false };
  }
  let sourceDate: DateString | undefined;
  switch (kind) {
    case RepeatKind.SameEverySunday:
      sourceDate = entries.findEarlier(date, () => true);
      break;
    case RepeatKind.SameAllMonth: {
      const firstOfMonth = getFirstDayOfMonth(date);
      sourceDate = entries.findEarlier(
        date,
        () => true,
        (candidate) => candidate < firstOfMonth,
      );
      break;
    }
    case RepeatKind.BySundayOfMonth: {
      const ordinal = getSundayOrdinal(date);
      sourceDate = entries.findEarlier(date, (candidate) => getSundayOrdinal(candidate) === ordinal);
      if (!sourceDate && ordinal === 5) {
        return getBorrowedFirstSunday(query);
      }
      break;
    }
    default:
      ensureUnreachable(kind);
  }
  return {
    entry: sourceDate ? shownEntry(entries.get(sourceDate)) : undefined,
    isSet,
    sourceDate,
    borrowsFirstSunday: false,
  };
}

// A By-Sunday-of-the-month 5th Sunday that holds nothing, with no earlier 5th Sunday holding something: what its own
// month's 1st Sunday shows, resolved as of the same clock and repeatFrom (see the module comment). Its source is that
// 1st Sunday when it holds something, else the Sunday that fills it; nothing borrowed when it shows nothing.
function getBorrowedFirstSunday(query: RepeatingCellQuery): RepeatingCell {
  const firstSunday = addDayOffset(query.date, -28);
  const first = resolveRepeatingCell({ ...query, date: firstSunday });
  const sourceDate = first.isSet ? firstSunday : first.sourceDate;
  if (sourceDate === undefined) {
    return { isSet: false, borrowsFirstSunday: false };
  }
  return { entry: first.entry, isSet: false, sourceDate, borrowsFirstSunday: true };
}

// the last Sunday of the month holding `date`
function getLastSundayOfMonth(date: DateString): DateString {
  return mostRecentSunday(getLastDayOfMonth(date));
}

// The last Sunday an edit on `date` shows through, up to `lastSunday` (the planner's last column): `date` itself in a
// row that keeps per-Sunday cells (Does not repeat, By Sunday of the month); for Same every Sunday the Sunday before
// the next one that holds something, or `lastSunday`; for Same all month the month's last Sunday, whatever the later
// Sundays of the month hold, since an edit there takes their own entries off (issue #4210). Two Sundays edit alone in
// every kind: a past one, since the edit rules keep the current Sunday, and one before the row's `repeatFrom` (a
// repeatFrom later than the current Sunday, as a clock or time-zone difference can give), which the fill does not
// reach and whose edit therefore takes nothing off the rest of its month either.
export function getReachEnd({
  kind,
  date,
  currentSunday,
  repeatFrom,
  entries,
  lastSunday,
}: RepeatingCellQuery & { lastSunday: DateString }): DateString {
  if (date < currentSunday || (repeatFrom !== undefined && date < repeatFrom)) {
    return date;
  }
  switch (kind) {
    case RepeatKind.DoesNotRepeat:
    case RepeatKind.BySundayOfMonth:
      return date;
    case RepeatKind.SameEverySunday: {
      const next = entries.findLater(date);
      const end = next && next <= lastSunday ? addDayOffset(next, -7) : lastSunday;
      return end < date ? date : end;
    }
    case RepeatKind.SameAllMonth: {
      const monthEnd = getLastSundayOfMonth(date);
      const end = monthEnd < lastSunday ? monthEnd : lastSunday;
      return end < date ? date : end;
    }
    default:
      ensureUnreachable(kind);
  }
}

// The song a starred Same-all-month row holds for the month starting `firstOfMonth`. A month not wholly past reads it
// on the month's first Sunday that is not past: `currentSunday` in the current month, the month's first Sunday for a
// future month, and the row's fill applies there. A month wholly past reads the latest of its Sundays that holds a
// song (a blank shows none and is skipped), since the month's song is usually typed on one Sunday and reaches the
// later ones by the fill, and an edit there runs to the month's end; undefined when none does. The fill window
// (getFillStartSunday) does not matter here: a month not wholly past resolves on or after the current Sunday.
function getHymnOfTheMonthEntry(
  firstOfMonth: DateString,
  currentSunday: DateString,
  repeatFrom: DateString | undefined,
  entries: RowEntries,
): SongListEntry | undefined {
  const lastSunday = getLastSundayOfMonth(firstOfMonth);
  if (currentSunday > lastSunday) {
    const sourceDate = entries.findEarlier(
      addDayOffset(lastSunday, 1),
      (candidate) => !isBlankEntry(entries.get(candidate)),
      (candidate) => candidate < firstOfMonth,
    );
    return sourceDate ? entries.get(sourceDate) : undefined;
  }
  const firstSunday = thisSundayDate(firstOfMonth);
  const { entry } = resolveRepeatingCell({
    kind: RepeatKind.SameAllMonth,
    date: currentSunday < firstSunday ? firstSunday : currentSunday,
    currentSunday,
    repeatFrom,
    entries,
  });
  return entry;
}

// as of when a reader reads: `today` is the reader's date (thisSundayDate of it is the current Sunday), `now` the
// instant that says whether the Sunday before is written down yet when `writtenThrough` is unknown, and
// `writtenThrough` the last Sunday the server has written the church down through (GET /api/passed-sundays), which
// says it for every passed Sunday (getFillStartSunday)
export interface FillClock {
  today?: DateString;
  now?: Date;
  writtenThrough?: DateString;
}

// The current Sunday and the first Sunday the fill applies to, as of `clock`. With neither `today` nor `now` given,
// both come from the wall clock; with `now` alone, `today` is its local date. With `writtenThrough`, the fill starts
// after it (getFillStartSunday). Without it, `today` alone reads as of a date rather than an instant, which need not be
// the wall clock's (a server reading a past or future Sunday), so the fill starts at the current Sunday as it did
// before issue #4192.
function getFillWindow({ today, now, writtenThrough }: FillClock): {
  currentSunday: DateString;
  fillStart: DateString;
} {
  if (today !== undefined && now === undefined && writtenThrough === undefined) {
    const currentSunday = thisSundayDate(today);
    return { currentSunday, fillStart: currentSunday };
  }
  const instant = now ?? new Date();
  const currentSunday = getCurrentSunday({ today, now: instant });
  return { currentSunday, fillStart: getFillStartSunday(instant, currentSunday, writtenThrough) };
}

// the current Sunday as of `clock` (see getFillWindow)
function getCurrentSunday({ today, now }: FillClock): DateString {
  return thisSundayDate(today ?? dateStringFromDate(now ?? new Date()));
}

// The songs `date` shows: the weekly list's entries without its blanks (issue #4190), then, for each repeating row the
// Sunday holds nothing at, the entry the row fills in from an earlier Sunday (see resolveRepeatingCell), sorted by
// row; a weekday, and a Sunday before the fill starts (getFillStartSunday), fills nothing. The stored entries come
// back as they are when nothing fills in and they hold no blank. Any other org's weekly songs come back as stored: a
// school's `text: ''` entry is not a blank
export function getWeeklySongEntriesForDate(
  org: Org,
  date: DateString,
  songLists: SongList[],
  clock: FillClock = {},
): SongListEntry[] {
  const weeklySongs = songLists.find((list) => isWeeklySongListForDate(org, list, date))?.songs ?? [];
  if (org.type !== OrgType.Church) {
    return weeklySongs;
  }
  const { currentSunday, fillStart } = getFillWindow(clock);
  const filled: SongListEntry[] = [];
  if (date >= fillStart && getDayOfWeek(date) === DayOfWeek.Sunday) {
    for (const row of org.churchLiturgySchema?.rows ?? []) {
      const kind = getRepeatKind(row);
      if (kind === RepeatKind.DoesNotRepeat || weeklySongs.some((song) => song.row === row.row)) {
        continue;
      }
      const entries = getRowEntries(row.row, songLists);
      const { entry } = resolveRepeatingCell({
        kind,
        date,
        currentSunday,
        fillStart,
        repeatFrom: getRepeatFrom(row),
        entries,
      });
      if (entry) {
        filled.push(entry);
      }
    }
  }
  const songs = withoutBlankEntries(weeklySongs);
  if (filled.length === 0) {
    return songs;
  }
  return [...songs, ...filled].sort((lhs, rhs) => lhs.row - rhs.row);
}

// true when two entries hold the same song (see isSameSong), or when both are missing
export function isSameEntry(lhs: SongListEntry | undefined, rhs: SongListEntry | undefined): boolean {
  if (!lhs || !rhs) {
    return !lhs && !rhs;
  }
  return isSameSong(lhs, rhs);
}

// The hymns of the month for `date`: the hymn of the month list itself (when there is one), then, for each of a
// church's Hymn of the Month rows (isHymnOfTheMonthRow: Song-type Same-all-month rows shown as Hymn of the Month), a
// synthesized single-song hymn of the month list holding the row's song for the month (see getHymnOfTheMonthEntry), so
// every reader of hymn of the month lists picks it up. A song already collected is skipped, and a blank (issue #4190)
// is no song: a church's hymn of the month list comes back without its blanks.
export function getHymnsOfTheMonthForDate(
  org: Org,
  date: DateString,
  songLists: SongList[],
  clock: FillClock = {},
): HymnOfTheMonthSongList[] {
  const firstOfMonth = getHymnOfTheMonthDate(date);
  const hymnsOfTheMonth: HymnOfTheMonthSongList[] = [];
  const hymnOfTheMonth = songLists.find(
    (list): list is HymnOfTheMonthSongList => list.type === SongListType.HymnOfTheMonth && list.date === firstOfMonth,
  );
  if (hymnOfTheMonth) {
    const songs = org.type === OrgType.Church ? withoutBlankEntries(hymnOfTheMonth.songs) : hymnOfTheMonth.songs;
    hymnsOfTheMonth.push(songs === hymnOfTheMonth.songs ? hymnOfTheMonth : { ...hymnOfTheMonth, songs });
  }
  if (org.type !== OrgType.Church) {
    return hymnsOfTheMonth;
  }
  const starredRows = (org.churchLiturgySchema?.rows ?? [])
    .filter(isHymnOfTheMonthRow)
    .sort((lhs, rhs) => lhs.row - rhs.row);
  const currentSunday = getCurrentSunday(clock);
  for (const row of starredRows) {
    const entry = getHymnOfTheMonthEntry(
      firstOfMonth,
      currentSunday,
      getRepeatFrom(row),
      getRowEntries(row.row, songLists),
    );
    if (!entry || hymnsOfTheMonth.some(({ songs }) => songs.some((song) => isSameEntry(song, entry)))) {
      continue;
    }
    hymnsOfTheMonth.push({
      type: SongListType.HymnOfTheMonth,
      date: firstOfMonth,
      interval: { type: IntervalType.Month, start: firstOfMonth, end: getLastDayOfMonth(firstOfMonth) },
      songs: [{ ...entry, row: 1 }],
    });
  }
  return hymnsOfTheMonth;
}
